import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T } from 'gt-react';
import type { PricelistCaseListRow } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';
import {
  SetNextStepDialog,
  type SetNextStepValues,
} from '@/components/common/waiting-on/SetNextStepDialog';
import {
  caseVendorsFromLegacy,
  formatPricelistCaseVendorNames,
} from './pricelistCaseVendors';
import { usePricelistCaseWaitingOnOptions } from './pricelistCaseWaitingOnOptions';
import { invalidatePricelistCaseQueries } from './pricelistCaseQueryInvalidation';

/**
 * Pricelist update cases' binding of the shared Set Next Step dialog: supplies
 * the org's IM waiting-on options and the case mutation, and nothing else —
 * the form, the reason picker, the follow-up date + time and the note all come
 * from the shared control, as they do for AR collection threads.
 *
 * The subtitle is the case number and the vendor, which is how a price change
 * is named in conversation.
 */
export function PricelistCaseSetNextStepDialog({
  pricelistCase,
  onOpenChange,
}: {
  /**
   * The case being parked; null keeps the dialog closed. A LIST row suffices
   * (the detail response extends it), so the cases table can open this
   * dialog per row without fetching each case.
   */
  pricelistCase: PricelistCaseListRow | null;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const options = usePricelistCaseWaitingOnOptions();
  const setNextStep = useMutation(
    orpc.inventoryManagement.pricelistCases.setNextStep.mutationOptions(),
  );
  const caseId = pricelistCase?.id;

  return (
    <SetNextStepDialog
      record={
        pricelistCase
          ? {
              id: pricelistCase.id,
              referenceCode: pricelistCase.caseNumber ?? '',
              title: formatPricelistCaseVendorNames(
                caseVendorsFromLegacy(
                  pricelistCase.companyId,
                  pricelistCase.companyName,
                  pricelistCase.vendors,
                ),
                pricelistVendorName,
              ),
              waitingOnKey: pricelistCase.waitingOnKey,
              followUpAt: pricelistCase.followUpAt
                ? new Date(pricelistCase.followUpAt).toISOString()
                : null,
            }
          : null
      }
      options={options}
      isSaving={setNextStep.isPending}
      noteHint={
        <T context="hint under the next-step note field on a pricelist update case">
          Posted as a note on the price change. Doesn't notify watchers.
        </T>
      }
      onSave={async (values: SetNextStepValues) => {
        // Unreachable: the dialog only saves while a case is open.
        if (caseId === undefined) return;
        await setNextStep.mutateAsync({ caseId, ...values });
        invalidatePricelistCaseQueries(queryClient, {
          comments: Boolean(values.note),
        });
      }}
      onOpenChange={onOpenChange}
    />
  );
}
